import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

// Google-only sign-in (production parity). Defaults ON; set
// VITE_AUTH_GOOGLE_ONLY=false to re-enable the password register/recover pages.
const GOOGLE_ONLY_AUTH =
  (((import.meta as any).env?.VITE_AUTH_GOOGLE_ONLY ?? 'true') as string) !== 'false'
import { Provider, useSelector } from 'react-redux'
import { store, type RootState } from './store'
import { ThemeProvider } from './components/providers/ThemeProvider'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { MFAEnforcementBanner } from './components/auth/MFAEnforcementBanner'
import { Layout } from './components/layout/Layout'
import { Dashboard } from './pages/Dashboard'
import { ProgressAnalytics } from './pages/ProgressAnalytics'
import { Dreams } from './pages/Dreams'
import { DreamDetail } from './pages/DreamDetail'
import { CreateDream } from './pages/CreateDream'
// Legacy aspiration pages — routes redirect to /goals
import { Skills } from './pages/Skills'
import { SkillDetail } from './pages/SkillDetail'
import { CreateSkill } from './pages/CreateSkill'
import { Habits } from './pages/Habits'
import { HabitDetail } from './pages/HabitDetail'
import { CreateHabit } from './pages/CreateHabit'
import { HabitOccurrencePage } from './pages/HabitOccurrencePage'
import { Goals } from './pages/Goals'
import { GoalDetail } from './pages/GoalDetail'
import { CreateGoal } from './pages/CreateGoal'
import { Tasks } from './pages/Tasks'
import { TaskDetail } from './pages/TaskDetail'
import { CreateTask } from './pages/CreateTask'
import { ScheduleV2 } from './pages/ScheduleV2'
import { Places } from './pages/Places'
import { Billing } from './pages/Billing'
import { HortonChat } from './pages/HortonChat'
import { Profile } from './pages/Profile'
import { DevTools } from './pages/DevTools'
import { LifeMapPage } from './pages/LifeMapPage'
import { TodayFocus } from './pages/TodayFocus'
import { TodayWhy } from './pages/TodayWhy'
import { MyPeople } from './pages/MyPeople' // the people manager (formerly MyWorld)
import { HowHortonHelps } from './pages/HowHortonHelps' // guidance calibration profile
import { Welcome } from './pages/Welcome' // first-run setup: the five questions that make the app fit
import { MotivationPage } from './pages/MotivationPage'
import { Notifications } from './pages/Notifications' // full alert history (bell > See all)
import { ReviewSchedule } from './pages/ReviewSchedule'
import { Login } from './pages/Login'
import { Register } from './pages/Register'
import { ForgotPassword } from './pages/ForgotPassword'
import { ResetPassword } from './pages/ResetPassword'
import { ChangePassword } from './pages/ChangePassword'
import { VerifyEmail } from './pages/VerifyEmail'
import { MFASetup } from './pages/MFASetup'
import { MFAChallenge } from './pages/MFAChallenge'
import { DownloadApp } from './pages/DownloadApp'
import { NotFound } from './pages/NotFound'
import { AdminPanel } from './pages/AdminPanel'
import { AdminUserPurge } from './pages/AdminUserPurge'
import { ThemeBuilder } from './pages/ThemeBuilder'
import Diary from './pages/Diary'
import PersonPage from './pages/PersonPage'
import DiaryShowcase from './pages/DiaryShowcase'
import WithYouSettings from './components/with-you/WithYouSettings'
import { UserTimezoneProvider } from './hooks/useUserTimezone'
import { ToastProvider } from './components/ui/Toast'
import { useCoachAlerts } from './hooks/useCoachAlerts'
import { SharedPollingProvider } from './providers/SharedPollingProvider'
import { InterruptiveAlertModal } from './components/alerts/InterruptiveAlertModal'
import { lazy, Suspense, useState, useEffect } from 'react'
import './index.css'

// Lazy-loaded so non-dev users never download the lab bundle.
const OrchestratorLab = lazy(() => import('./pages/OrchestratorLab'))
const AutoMomActivityLog = lazy(() => import('./pages/AutoMomActivityLog'))

// Create a client
function HortonRedirect() {
  const location = useLocation()
  return <Navigate to={`/horton-ai${location.search}`} replace />
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (react-query v5 name for cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

/**
 * Catch-all fallback that keeps unknown routes from leaking "this path
 * exists / doesn't exist" to the world. If the user has a session, we
 * render the standard NotFound page (useful context: they navigated
 * somewhere valid-looking that just isn't a real route). If they don't,
 * we bounce them through /login with ?redirect= preserved, matching the
 * exact flow in ProtectedRoute so they land back on the bogus path
 * after login (where NotFound is the correct terminal view).
 */
function AuthAwareNotFound() {
  const isAuthenticated = useSelector(
    (s: RootState) => s.auth.isAuthenticated,
  )
  const location = useLocation()
  if (!isAuthenticated) {
    const intended = `${location.pathname}${location.search}${location.hash}`
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(intended)}`}
        replace
      />
    )
  }
  return <NotFound />
}

function App() {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <ToastProvider>
            <UserTimezoneProvider>
              <Router>
                {/* One poller per app-wide feed (alerts, server clock,
                    notifications). Inside the Router: the alerts feed
                    refetches on route change. See SharedPollingProvider. */}
                <SharedPollingProvider>
                  <AppWithAlerts />
                </SharedPollingProvider>
              </Router>
            </UserTimezoneProvider>
          </ToastProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </Provider>
  )
}

/**
 * Inner component with router context for alert modal integration.
 */
function AppWithAlerts() {
  // Subscribe to route changes so alert candidacy re-evaluates on navigation
  useLocation()
  const { interruptiveCandidate, markShown, dismiss, snooze, acknowledge } = useCoachAlerts()
  const [sessionShownIds, setSessionShownIds] = useState<string[]>(() => {
    const stored = sessionStorage.getItem('horton-shown-alert-ids')
    return stored ? stored.split(',').filter(Boolean) : []
  })

  // Determine if modal should show
  const shouldShowModal = interruptiveCandidate && !sessionShownIds.includes(interruptiveCandidate.id)

  // Detect context (login, refresh, or route_change)
  const getContext = (): 'login' | 'refresh' | 'route_change' => {
    // Check if this is a hard refresh
    if (performance && performance.navigation && (performance.navigation as any).type === 1) {
      return 'refresh'
    }
    // Check if user just logged in (heuristic: first page load after auth)
    const isFirstLoad = sessionStorage.getItem('horton-first-load') !== 'false'
    if (isFirstLoad) {
      sessionStorage.setItem('horton-first-load', 'false')
      return 'login'
    }
    return 'route_change'
  }

  // When interruptive candidate appears, mark as shown and track in session
  useEffect(() => {
    if (shouldShowModal && interruptiveCandidate) {
      const context = getContext()
      
      // Mark shown on backend
      markShown(interruptiveCandidate.id, context)
      
      // Track in session storage to prevent re-showing
      setSessionShownIds(prev => {
        const updated = [...prev, interruptiveCandidate.id]
        sessionStorage.setItem('horton-shown-alert-ids', updated.join(','))
        return updated
      })
    }
  }, [interruptiveCandidate?.id, shouldShowModal, markShown])

  const handleAction = () => {
    if (interruptiveCandidate?.action?.url) {
      window.location.href = interruptiveCandidate.action.url
    }
  }

  const handleDismiss = () => {
    if (interruptiveCandidate) {
      dismiss(interruptiveCandidate.id)
    }
  }

  const handleSnooze = (minutes: number) => {
    if (interruptiveCandidate) {
      snooze(interruptiveCandidate.id, minutes)
    }
  }

  const handleAcknowledge = () => {
    if (interruptiveCandidate) {
      acknowledge(interruptiveCandidate.id)
    }
  }

  return (
    <>
      <div className="min-h-screen bg-surface text-primary">
        <Routes>
          {/* Public routes. In Google-only mode (production parity) registration
              is a "Sign up with Google" page (new accounts land pending admin
              approval); password recovery pages are collapsed into login since
              there are no passwords. */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="/forgot-password"
            element={GOOGLE_ONLY_AUTH ? <Navigate to="/login" replace /> : <ForgotPassword />}
          />
          <Route
            path="/reset-password"
            element={GOOGLE_ONLY_AUTH ? <Navigate to="/login" replace /> : <ResetPassword />}
          />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/mfa-challenge" element={<MFAChallenge />} />

          {/*
            Full-page shell (no Layout). Extra wrapper: `fixed` + `overflow-y-auto`
            guarantees the document scrolls on all browsers; nested flex shells
            (Layout main / mobile h-dvh) were still clipping this page for some users.
            APK files remain public at /downloads/*.apk (nginx).
          */}
          <Route
            path="/download"
            element={
              <ProtectedRoute>
                <div
                  className="fixed inset-0 z-40 overflow-x-hidden overflow-y-auto overscroll-y-contain"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                >
                  <DownloadApp />
                </div>
              </ProtectedRoute>
            }
          />

          {/*
            First run. Protected, but deliberately outside Layout: on the very
            first visit the rail's forty destinations are noise, and the five
            questions here are what make every later suggestion land in the
            person's real day (docs/PROFILE_UX_PASS_2026-09-06.md).
          */}
          <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />

          {/* Protected routes */}
          <Route path="/" element={<ProtectedRoute><MFAEnforcementBanner /><Layout /></ProtectedRoute>}>
            <Route index element={<TodayFocus />} />
            <Route path="today" element={<TodayFocus />} />
            {/* The pull surface: /today stays one-at-a-time and veiled;
                this is the full inventory, only ever reached on purpose. */}
            <Route path="today/why" element={<TodayWhy />} />
            <Route path="motivation" element={<MotivationPage />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="dreams" element={<Dreams />} />
            <Route path="dreams/create" element={<CreateDream />} />
            <Route path="dreams/:id" element={<DreamDetail />} />
            <Route path="dreams/:id/edit" element={<Navigate to="../" replace />} />
            {/* The aspiration layer between dreams and goals is gone
                (owner): dreams hold goals; goals hold skills, habits, tasks. */}
            <Route path="aspirations/*" element={<Navigate to="/goals" replace />} />
            <Route path="skills" element={<Skills />} />
            <Route path="skills/create" element={<CreateSkill />} />
            <Route path="skills/:id" element={<SkillDetail />} />
            <Route path="habits" element={<Habits />} />
            <Route path="habits/create" element={<CreateHabit />} />
            <Route path="habits/occurrences/:occurrenceId" element={<HabitOccurrencePage />} />
            <Route path="habits/:id" element={<HabitDetail />} />
            <Route path="goals" element={<Goals />} />
            <Route path="goals/create" element={<CreateGoal />} />
            <Route path="goals/:id" element={<GoalDetail />} />
            <Route path="goals/:id/edit" element={<Navigate to="../" replace />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="tasks/create" element={<CreateTask />} />
            <Route path="tasks/:id" element={<TaskDetail />} />
            {/* Action items are steps under a task, not a second to-do list
                (owner 2026-09-06). No pages of their own; they render as rows
                on /tasks and as steps on the task they belong to. */}
            <Route path="action-items/*" element={<Navigate to="/tasks" replace />} />
            {/* The new schedule is THE schedule (owner ask 2026-08-25);
                the v1 page is retired and the old URL keeps working. */}
            <Route path="schedule" element={<ScheduleV2 />} />
            <Route path="schedule-v2" element={<Navigate to="/schedule" replace />} />
            <Route path="review-schedule" element={<ReviewSchedule />} />
            <Route path="scheduling-preferences" element={<Navigate to="/profile?tab=scheduling" replace />} />
            <Route path="places" element={<Places />} />
            {/* Roadblocks are no longer tracked separately (owner 2026-09-06):
                what gets in the way is recorded on the item it blocks, and the
                engine proposes a task, habit or skill to get past it. */}
            <Route path="roadblocks/*" element={<Navigate to="/tasks" replace />} />
            <Route path="progress" element={<Dashboard />} />
            {/* Your rhythm (owner 2026-09-15: "display my analytics page in the ui") */}
            <Route path="analytics" element={<ProgressAnalytics />} />
            {/* Common bookmark/legacy URL — app nav uses "Progress" at /dashboard */}
            <Route path="stats" element={<Navigate to="/dashboard" replace />} />
            <Route path="billing" element={<Billing />} />
            <Route path="horton-ai" element={<HortonChat />} />
            <Route path="horton" element={<HortonRedirect />} />
            {/* Canonical whole-hierarchy view (Hierarchy review UX-2/UX-12).
                The former /my-hierarchy (read-only outline, dead-end clicks)
                and /hierarchy-treemap (orphaned treemap) views were
                consolidated into the graph-based Life Map — the DreamGraphView
                engine already embedded on every entity detail page and the only
                view with working drill-through. Old URLs redirect so bookmarks
                and search still land. */}
            <Route path="life-map" element={<LifeMapPage />} />
            <Route path="my-hierarchy" element={<Navigate to="/life-map" replace />} />
            <Route path="hierarchy-treemap" element={<Navigate to="/life-map" replace />} />
            {/* The My World overview page was dropped from the nav (owner ask
                2026-07-05: not useful); deep links land on the people manager. */}
            <Route path="my-world" element={<Navigate to="/my-people" replace />} />
            <Route path="my-people" element={<MyPeople />} />
            {/* One person, with everything dated. `person/` in the path so a
                future /my-people/:tab cannot collide with an entity id. */}
            <Route path="my-people/person/:entityId" element={<PersonPage />} />
            {/* One inbox with two tabs (2026-09-06): there used to be three. */}
            <Route path="horton-inbox" element={<Navigate to="/notifications?tab=decide" replace />} />
            <Route path="how-horton-helps" element={<HowHortonHelps />} />
            {/* What was I told, and when — the bell's history. Deliberately
                separate from the dashboard activity feeds, which answer the
                different question of what happened. */}
            <Route path="notifications" element={<Notifications />} />
            <Route path="profile" element={<Profile />} />
            <Route path="with-you/settings" element={<WithYouSettings />} />
            <Route path="theme-builder" element={<ThemeBuilder />} />
            {/* The diary: a second surface, not the chat. Someone writes
                their day; the engine holds anything commitment-shaped until
                it recurs across days, so a passing Tuesday never lands on the
                Life Map. (/diary-showcase below is the THEME gallery — an
                unrelated page with a confusingly similar name.) */}
            <Route path="diary" element={<Diary />} />
            <Route path="diary-showcase" element={<DiaryShowcase />} />
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="admin" element={<AdminPanel />} />
            <Route path="admin/purge" element={<AdminUserPurge />} />
            <Route path="dev-tools" element={<DevTools />} />
            {/* The assistant lab was removed (owner ask 2026-08-25); the
                activity log remains the admin window into it. */}
            <Route path="dev/automom-lab" element={<Navigate to={import.meta.env.DEV ? "/dev/automom-activity-log" : "/today"} replace />} />
            <Route
              path="dev/orchestrator-lab"
              element={
                <Suspense fallback={<div className="p-4 text-slate-500">Loading Orchestrator Lab…</div>}>
                  <OrchestratorLab />
                </Suspense>
              }
            />
            {/* DEV/RIG ONLY (owner ask 2026-08-30). `import.meta.env.DEV` is
                false in every production build, so this route is not merely
                hidden — it is never registered, and /dev/automom-activity-log
                falls through to the 404 route like any other unknown path.
                The backing endpoint is gated independently behind
                HORTON_LAB_ENDPOINTS_ENABLED (automom_routes.py), because a
                hidden page in front of a live endpoint is decoration. */}
            {import.meta.env.DEV && (
              <Route
                path="dev/automom-activity-log"
                element={
                  <Suspense fallback={<div className="p-4 text-slate-500">Loading activity log…</div>}>
                    <AutoMomActivityLog />
                  </Suspense>
                }
              />
            )}
            {/* Retired demo surface (E12): nothing in the app linked here. */}
            <Route path="time-travel" element={<Navigate to="/dashboard" replace />} />
            <Route path="time-travel/player" element={<Navigate to="/dashboard" replace />} />
            {/*
              /mfa-setup is auth-gated. Both API calls the page makes
              (/api/mfa/status and /api/mfa/setup) require a session,
              and every legitimate entry point routes through an authed
              surface:
                - Profile > Security > "Manage 2FA" / "Enable 2FA Now"
                  (see src/pages/Profile.tsx renderSecuritySettings)
              An anonymous visit previously rendered the page and
              immediately 401'd on /api/mfa/status, leaving the user
              on a broken shell. Gating it here gives them the same
              /login?redirect=%2Fmfa-setup flow every other internal
              page has.
            */}
            <Route path="mfa-setup" element={<MFASetup />} />
          </Route>
          
          {/* 404 route (auth-aware: unauth visitors bounce to /login?redirect=) */}
          <Route path="*" element={<AuthAwareNotFound />} />
        </Routes>
      </div>

      {/* Interruptive Alert Modal - Session-gated, rate-limited */}
      {shouldShowModal && interruptiveCandidate && (
        <InterruptiveAlertModal
          alert={interruptiveCandidate}
          onAction={handleAction}
          onDismiss={handleDismiss}
          onSnooze={handleSnooze}
          onAcknowledge={handleAcknowledge}
        />
      )}
    </>
  )
}

export default App